import { useEffect } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import PageContext from 'shared/util/PageContext';
import ThemeContext from 'shared/util/ThemeContext';
import WidgetConnectorNew from '../SiteBuilder/WidgetConnector';
import styled, { keyframes } from 'styled-components';
import BuilderTopMenu from 'builder/components/BuilderTopMenu';
import Feedback from 'shared/components/Feedback/Feedback';
import { User } from 'shared/state/misc/oidc';
import { isContentLoaded } from 'shared/util/pageUtils';
import { TEMPLATE_EDITOR_PAGE_ID } from '../PageTemplates/constants';
import UserSyncChangesDialogContainer from 'shared/components/UserSycChangesDialog/UserSyncChangesDialogContainer';

interface PreviewProps {
  site: Site;
  pages: Array<Page>;
  basePath: string;
  user: User;
  updateCurrentPage: (pageVersion: PageVersion) => void;
  fetchPage: (pageId: number) => void;
}

const spinAnimation = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const PageLoadingSpinner = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.5);

  &::after {
    content: '';
    width: 48px;
    height: 48px;
    border: 4px solid #e0e0e0;
    border-top-color: #002677;
    border-radius: 50%;
    animation: ${spinAnimation} 0.8s linear infinite;
  }
`;

const PageLoader = ({
  pageId,
  isLoaded,
  fetchPage,
}: {
  pageId: number;
  isLoaded: boolean;
  fetchPage: (id: number) => void;
}) => {
  useEffect(() => {
    if (!isLoaded) {
      fetchPage(pageId);
    }
  }, [pageId, isLoaded, fetchPage]);
  return null;
};

const PreviewContainer = styled.div`
  position: relative;
`;

const StyledPreview = styled.div``;

const Preview = ({
  site,
  pages,
  user,
  basePath,
  updateCurrentPage,
  fetchPage,
}: PreviewProps) => {
  if (!site) {
    return <Redirect to="/sites" />;
  }

  const renderContent = (page: Page) => (
    <PageContext.Provider
      value={{
        site,
        pages,
        page,
      }}
    >
      <PageLoader
        pageId={page.id}
        isLoaded={isContentLoaded(page.current)}
        fetchPage={fetchPage}
      />
      {!isContentLoaded(page.current) && (
        <PageLoadingSpinner
          data-test-id="preview-page-loading"
          aria-busy={true}
          role="status"
          aria-label="Loading page"
        />
      )}
      <BuilderTopMenu
        siteId={site.siteId}
        pageId={page.id}
        isPreviewMode={true}
      />
      <StyledPreview
        style={{
          backgroundColor: page.current.pageBackground?.color
            ? page.current.pageBackground?.color
            : '#FFFFFF',
        }}
      >
        <PreviewContainer>
          <WidgetConnectorNew
            currentPage={page.current}
            siteId={site.siteId}
            pageId={page.id}
            widgetId={''}
            page={page}
            site={site}
            updateCurrentPage={updateCurrentPage}
          />
          <Feedback
            value={site.header.feedback}
            siteId={site.siteId}
            siteName={site.name}
            user={user}
          />
        </PreviewContainer>
      </StyledPreview>
      <UserSyncChangesDialogContainer
        siteId={site.siteId}
        site={site}
        pageId={page.id}
        showToastMessage={true}
      />
    </PageContext.Provider>
  );

  const siteRoutes = pages
    .filter(page => page.current)
    .map(page => (
      <Route
        key={page.id}
        path={`${basePath}/${page.current.slug}`}
        exact
        render={() => renderContent(page)}
      />
    ));

  return (
    <ThemeContext.Provider
      value={{
        theme: site.theme,
      }}
    >
      <Switch>
        {siteRoutes}
        <Route
          path="/page-templates/:templateId/preview"
          exact
          render={() => {
            const page = pages.find(p => p.id === TEMPLATE_EDITOR_PAGE_ID);
            return page ? (
              renderContent(page)
            ) : (
              <Redirect to="/page-templates" />
            );
          }}
        />
        <Route>
          <Redirect
            to={`${basePath}/${
              pages
                .filter(page => page.current)
                .find(page => page.id === site.horizonhomepageId)?.current.slug
            }`}
          />
        </Route>
      </Switch>
    </ThemeContext.Provider>
  );
};

export default Preview;
